import React, { useEffect, useState } from "react";
import { Phone } from "lucide-react";

const BOX_STYLE = {
  height: 84,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 18,
  position: "relative",
};

const resolveAccent = (accent, fallback) => accent || fallback;

// === 1. Document Integrations (Doküman ve Uygulama Entegrasyonu) ===
export const WaveformAnim = ({ accent = "var(--brand-cyan)", accentAlt = "var(--brand-coral)" }) => {
  const primary = resolveAccent(accent, "var(--brand-cyan)");
  const secondary = resolveAccent(accentAlt, "var(--brand-coral)");

  /* Inline doc-like SVG icons */
  const DocIcon = ({ color, delay }) => (
    <svg
      width="22"
      height="26"
      viewBox="0 0 22 26"
      fill="none"
      style={{ animation: `docFloat 3s ease-in-out ${delay} infinite` }}
    >
      <rect x="0.5" y="0.5" width="17" height="21" rx="2.5" fill={`${color}18`} stroke={color} strokeWidth="1.2" />
      <rect x="5" y="6" width="10" height="1.4" rx="0.7" fill={color} opacity="0.6" />
      <rect x="5" y="9.5" width="8" height="1.4" rx="0.7" fill={color} opacity="0.6" />
      <rect x="5" y="13" width="10" height="1.4" rx="0.7" fill={color} opacity="0.6" />
      <rect x="5" y="16.5" width="6" height="1.4" rx="0.7" fill={color} opacity="0.6" />
    </svg>
  );

  const SheetIcon = ({ color, delay }) => (
    <svg
      width="22"
      height="26"
      viewBox="0 0 22 26"
      fill="none"
      style={{ animation: `docFloat 3s ease-in-out ${delay} infinite` }}
    >
      <rect x="0.5" y="0.5" width="17" height="21" rx="2.5" fill={`${color}18`} stroke={color} strokeWidth="1.2" />
      <line x1="0.5" y1="7.5" x2="17.5" y2="7.5" stroke={color} strokeWidth="0.8" opacity="0.4" />
      <line x1="0.5" y1="12.5" x2="17.5" y2="12.5" stroke={color} strokeWidth="0.8" opacity="0.4" />
      <line x1="9" y1="0.5" x2="9" y2="21.5" stroke={color} strokeWidth="0.8" opacity="0.4" />
      <rect x="3" y="14.5" width="5" height="3" rx="0.6" fill={color} opacity="0.35" />
      <rect x="10.5" y="14.5" width="5" height="5" rx="0.6" fill={secondary} opacity="0.45" />
    </svg>
  );

  const NotionIcon = ({ color, delay }) => (
    <svg
      width="22"
      height="26"
      viewBox="0 0 22 26"
      fill="none"
      style={{ animation: `docFloat 3s ease-in-out ${delay} infinite` }}
    >
      <rect x="0.5" y="0.5" width="17" height="21" rx="2.5" fill={`${color}18`} stroke={color} strokeWidth="1.2" />
      <rect x="4" y="5" width="10" height="3.5" rx="0.8" fill={color} opacity="0.5" />
      <rect x="4" y="10" width="10" height="1.2" rx="0.6" fill={color} opacity="0.3" />
      <rect x="4" y="12.5" width="10" height="1.2" rx="0.6" fill={color} opacity="0.3" />
      <rect x="4" y="15" width="10" height="1.2" rx="0.6" fill={color} opacity="0.3" />
      <rect x="4" y="17.5" width="6" height="1.2" rx="0.6" fill={secondary} opacity="0.45" />
    </svg>
  );

  const docs = [
    { icon: DocIcon, color: "#4285F4", delay: "0s" },      // Google Docs blue
    { icon: SheetIcon, color: "#0F9D58", delay: "0.35s" },   // Google Sheets green
    { icon: NotionIcon, color: primary, delay: "0.7s" },      // Notion-like
  ];

  return (
    <div style={BOX_STYLE}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 14, position: "relative" }}>
        {docs.map((d, i) => (
          <span
            key={i}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <d.icon color={d.color} delay={d.delay} />
            {/* small glow dot */}
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: d.color,
                opacity: 0.7,
                animation: `docDotPulse 1.6s ease-in-out ${d.delay} infinite`,
              }}
            />
          </span>
        ))}
        {/* central sync hub */}
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${primary}, ${secondary})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 20px ${primary}55`,
            animation: "docHubPulse 2s ease-in-out infinite",
            marginLeft: 4,
            marginBottom: 14,
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
          </svg>
        </span>
      </div>
      <style>{`
        @keyframes docFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-9px); }
        }
        @keyframes docDotPulse {
          0%, 100% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1.5); opacity: 1; }
        }
        @keyframes docHubPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};

// === 2. Phone Ringing (7/24 Telefon Karşılama) ===
export const PhoneAnim = ({ accent = "var(--brand-cyan)", accentAlt = "var(--brand-coral)" }) => {
  const primary = resolveAccent(accent, "var(--brand-cyan)");
  const secondary = resolveAccent(accentAlt, "var(--brand-coral)");
  return (
    <div style={BOX_STYLE}>
      <div className="relative" style={{ width: 56, height: 56 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              inset: -10 - i * 5,
              border: `1.5px solid ${i === 0 ? secondary : i === 1 ? primary : "var(--brand-cyan)"}`,
              opacity: 0,
              animation: `phRing 2.4s ease-out ${i * 0.6}s infinite`,
            }}
          />
        ))}
        <div
          className="absolute inset-0 rounded-2xl flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${primary}, ${secondary})`,
            color: "#fff",
            boxShadow: "0 6px 18px rgba(30,42,82,0.25)",
            animation: "phShake 2.4s ease-in-out infinite",
          }}
        >
          <Phone size={22} />
        </div>
        <span
          className="absolute -top-1 -right-1 rounded-full text-[10px] font-bold flex items-center justify-center"
          style={{
            width: 18,
            height: 18,
            background: secondary,
            color: "#fff",
            animation: "phBadge 2.4s ease-out infinite",
          }}
        >
          24
        </span>
      </div>
      <style>{`
        @keyframes phRing {
          0% { transform: scale(0.55); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes phShake {
          0%, 100% { transform: rotate(0); }
          10% { transform: rotate(-12deg); }
          20% { transform: rotate(10deg); }
          30% { transform: rotate(-8deg); }
          40% { transform: rotate(6deg); }
          50%, 100% { transform: rotate(0deg); }
        }
        @keyframes phBadge {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
};

// === 3. Languages Cycling (Çoklu Dil Desteği) ===
const LANGS = ["TR", "EN", "DE", "FR", "ES", "AR", "RU"];
export const LanguagesAnim = ({ accent = "var(--brand-cyan)", accentAlt = "var(--brand-coral)" }) => {
  const [idx, setIdx] = useState(0);
  const primary = resolveAccent(accent, "var(--brand-cyan)");
  const secondary = resolveAccent(accentAlt, "var(--brand-coral)");
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % LANGS.length), 1300);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={BOX_STYLE}>
      <div style={{ perspective: 600, minWidth: 88 }}>
        <div
          key={idx}
          className="rounded-xl text-[24px] font-bold tracking-[0.04em] text-center"
          style={{
            background: `linear-gradient(135deg, ${secondary}, ${primary})`,
            color: "#fff",
            padding: "10px 18px",
            animation: "lgFlip 0.55s ease",
            transformOrigin: "center bottom",
            boxShadow: "0 8px 18px rgba(75,111,212,0.18)",
          }}
        >
          {LANGS[idx]}
        </div>
      </div>
      <style>{`
        @keyframes lgFlip {
          0% { transform: rotateX(-95deg); opacity: 0; }
          60% { transform: rotateX(12deg); opacity: 1; }
          100% { transform: rotateX(0deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// === 4. Connecting Nodes (CRM ve Yardım Masası Entegrasyonu) ===
export const NodesAnim = ({ accent = "var(--brand-cyan)", accentAlt = "var(--brand-coral)" }) => {
  const primary = resolveAccent(accent, "var(--brand-cyan)");
  const secondary = resolveAccent(accentAlt, "var(--brand-coral)");
  return (
    <div style={BOX_STYLE}>
      <svg
        width="190"
        height="70"
        viewBox="0 0 190 70"
        style={{ overflow: "visible" }}
      >
        {/* Dashed lines */}
        <line
          x1="20"
          y1="35"
          x2="95"
          y2="35"
          stroke="var(--border)"
          strokeWidth="1.5"
          strokeDasharray="3 4"
        />
        <line
          x1="95"
          y1="35"
          x2="170"
          y2="14"
          stroke="var(--border)"
          strokeWidth="1.5"
          strokeDasharray="3 4"
        />
        <line
          x1="95"
          y1="35"
          x2="170"
          y2="56"
          stroke="var(--border)"
          strokeWidth="1.5"
          strokeDasharray="3 4"
        />
        {/* Animated pulses traveling along lines */}
        <circle r="3.5" fill={primary}>
          <animateMotion
            dur="2s"
            repeatCount="indefinite"
            path="M 20 35 L 95 35"
          />
        </circle>
        <circle r="3.5" fill={secondary}>
          <animateMotion
            dur="2s"
            repeatCount="indefinite"
            begin="0.5s"
            path="M 95 35 L 170 14"
          />
        </circle>
        <circle r="3.5" fill={secondary}>
          <animateMotion
            dur="2s"
            repeatCount="indefinite"
            begin="1.1s"
            path="M 95 35 L 170 56"
          />
        </circle>
        {/* Nodes */}
        <circle cx="20" cy="35" r="9" fill={secondary} />
        <circle
          cx="95"
          cy="35"
          r="12"
          fill={primary}
          stroke={primary}
          strokeWidth="2"
        />
        <circle cx="170" cy="14" r="8" fill={primary} />
        <circle cx="170" cy="56" r="8" fill={primary} />
        {/* Inner ping on central node */}
        <circle
          cx="95"
          cy="35"
          r="12"
          fill="none"
          stroke={secondary}
          strokeWidth="2"
          opacity="0.5"
        >
          <animate
            attributeName="r"
            values="12;22;12"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.5;0;0.5"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </div>
  );
};

// === 5. Social Media (Sosyal Medya Entegrasyonu) ===

/* Inline brand SVG paths */
const WhatsAppIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={color}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TelegramIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={color}>
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.94z" />
  </svg>
);

const InstagramIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={color}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

export const SocialMediaAnim = ({ accent = "var(--brand-cyan)", accentAlt = "var(--brand-coral)" }) => {
  const primary = resolveAccent(accent, "var(--brand-cyan)");
  const platforms = [
    { icon: WhatsAppIcon, color: "#25D366", delay: "0s" },
    { icon: TelegramIcon, color: "#0088cc", delay: "0.3s" },
    { icon: InstagramIcon, color: "#E4405F", delay: "0.15s" },
  ];
  return (
    <div style={BOX_STYLE}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          position: "relative",
        }}
      >
        {platforms.map((p, i) => (
          <span
            key={i}
            className="sm-icon-box"
            style={{
              width: 44,
              height: 44,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 14,
              background: `${p.color}18`,
              border: `1.5px solid ${p.color}45`,
              animationDelay: p.delay,
            }}
          >
            <p.icon color={p.color} />
          </span>
        ))}
        {/* Central hub */}
        <span
          className="sm-hub"
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: primary,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      </div>
      <style>{`
        .sm-icon-box {
          animation: smFloat 2.8s ease-in-out infinite;
        }
        .sm-hub {
          animation: smHubPulse 1.8s ease-in-out infinite;
        }
        @keyframes smFloat {
          0%, 100% { transform: translateY(0); }
          25% { transform: translateY(-8px); }
          75% { transform: translateY(4px); }
        }
        @keyframes smHubPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.8); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// === 6. Transcript Typewriter (Akıllı Transkript ve Özet) ===
export const TranscriptAnim = ({ accent = "var(--brand-cyan)", accentAlt = "var(--brand-coral)" }) => {
  const primary = resolveAccent(accent, "var(--brand-cyan)");
  const secondary = resolveAccent(accentAlt, "var(--brand-coral)");
  return (
    <div style={BOX_STYLE}>
      <div
        className="w-full flex flex-col gap-[7px]"
        style={{ maxWidth: 200, paddingLeft: 8, paddingRight: 8 }}
      >
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block rounded-sm"
            style={{
              width: 8,
              height: 8,
              background: secondary,
              flexShrink: 0,
            }}
          />
          <span
            className="block rounded-full"
            style={{
              height: 6,
              background: primary,
              animation: "txLine1 4s ease-out infinite",
            }}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block rounded-sm"
            style={{
              width: 8,
              height: 8,
              background: primary,
              flexShrink: 0,
            }}
          />
          <span
            className="block rounded-full"
            style={{
              height: 6,
              background: secondary,
              animation: "txLine2 4s ease-out 0.6s infinite",
            }}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block rounded-sm"
            style={{
              width: 8,
              height: 8,
              background: secondary,
              flexShrink: 0,
            }}
          />
          <span
            className="block rounded-full"
            style={{
              height: 6,
              background: "var(--brand-cyan)",
              animation: "txLine3 4s ease-out 1.2s infinite",
              opacity: 0.85,
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes txLine1 {
          0%, 6% { width: 0; opacity: 0; }
          14% { opacity: 1; }
          45%, 90% { width: 78%; opacity: 1; }
          100% { width: 78%; opacity: 0; }
        }
        @keyframes txLine2 {
          0%, 6% { width: 0; opacity: 0; }
          14% { opacity: 1; }
          45%, 90% { width: 58%; opacity: 1; }
          100% { width: 58%; opacity: 0; }
        }
        @keyframes txLine3 {
          0%, 6% { width: 0; opacity: 0; }
          14% { opacity: 0.85; }
          45%, 90% { width: 90%; opacity: 0.85; }
          100% { width: 90%; opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export const FEATURE_ANIM_MAP = {
  Mic: WaveformAnim,
  Phone: PhoneAnim,
  Languages: LanguagesAnim,
  Workflow: NodesAnim,
  Activity: SocialMediaAnim,
  FileText: TranscriptAnim,
};
